import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { useClerk, useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import pig1 from '../../assets/graphics/pig1.gif';
import { isAuthError } from '../utils/isAuthError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Inner component that uses hooks and handles the actual error display/recovery
const ErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({ error, resetError }) => {
  const { signOut, redirectToSignIn } = useClerk();
  const { isSignedIn } = useAuth();
  const { t } = useTranslation();

  const authError = isAuthError(error.message);

  // Check if this is a validation error (e.g., invalid Convex ID)
  const isValidationError =
    error.message?.includes('ArgumentValidationError') ||
    error.message?.includes('Value does not match validator');

  // Auto-redirect on auth errors — don't show an error dialog
  useEffect(() => {
    if (!authError) return;
    resetError();
    const returnPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('auth_return_path', returnPath);

    if (isSignedIn) {
      signOut({ redirectUrl: window.location.origin });
    } else {
      redirectToSignIn();
    }
  }, [authError, isSignedIn, signOut, redirectToSignIn, resetError]);

  // While redirecting on auth error, show nothing
  if (authError) {
    return null;
  }

  // For validation errors (invalid IDs, etc.), show a not found style page with pig
  if (isValidationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
        <div className="bg-card border-2 border-border/50 rounded-3xl p-8 md:p-12 shadow-lg text-center max-w-lg">
          <h1 className="text-8xl md:text-9xl font-bold text-primary/20 mb-4">404</h1>
          <div className="flex justify-center mb-6">
            <img
              src={pig1}
              alt="Gruntled mascot"
              className="w-48 h-48 md:w-60 md:h-60 object-contain"
            />
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
            {t('error.pageNotFoundTitle')}
          </h2>
          <p className="text-muted-foreground mb-8">
            {t('error.pageNotFoundMessage')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-secondary text-secondary-foreground font-medium rounded-full hover:bg-secondary/80 transition-colors"
            >
              {t('common.goBack')}
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-primary text-primary-foreground font-medium rounded-full hover:bg-primary/90 transition-colors"
            >
              {t('common.backHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For non-auth errors, show a generic error with retry option
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          {t('error.genericErrorTitle')}
        </h2>
        <p className="text-gray-600 mb-6">
          {error.message || 'An unexpected error occurred.'}
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={resetError}
            className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            {t('error.tryAgain')}
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-primary-5 text-white px-6 py-2 rounded-md hover:bg-primary-6 transition-colors"
          >
            {t('common.backHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

class ConvexErrorBoundaryClass extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
  }

  private resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError && this.state.error) {
      return <ErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// Wrapper to provide the class component with context
export const ConvexErrorBoundary: React.FC<Props> = ({ children }) => {
  return (
    <ConvexErrorBoundaryClass>
      {children}
    </ConvexErrorBoundaryClass>
  );
};
